import { useEffect, useState, useRef } from 'react';
import { authApi } from '../../api/auth.js';

/**
 * Security settings page — currently houses the MFA enrollment wizard.
 *
 * Visit /settings/security to enable, manage, or disable 2FA.
 */
export default function SecuritySettings() {
  const [status, setStatus] = useState(null);
  const [wizard, setWizard] = useState(null); // null | { step, qr, secret, backupCodes? }
  const [error, setError] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const reload = () => authApi.mfaStatus().then(setStatus).catch(() => {});
  useEffect(() => { reload(); }, []);

  async function startSetup() {
    try {
      setError(null);
      const data = await authApi.mfaSetup();
      setWizard({ step: 1, qr: data.qr_data_url, secret: data.secret });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start MFA setup');
    }
  }

  async function confirmCode(code) {
    try {
      setError(null);
      const data = await authApi.mfaConfirm(code);
      setWizard((w) => ({ ...w, step: 3, backupCodes: data.backup_codes }));
      reload();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code');
      throw err;
    }
  }

  async function disableMfa() {
    try {
      setError(null);
      await authApi.mfaDisable(disableCode);
      setConfirmDisable(false);
      setDisableCode('');
      reload();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disable MFA');
    }
  }

  return (
    <section style={{ padding: 24, maxWidth: 720 }}>
      <h1>Security settings</h1>

      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Two-factor authentication</h3>
        {!status && <div className="muted">Loading…</div>}

        {status && !status.enabled && !wizard && (
          <>
            <p className="muted">Add an extra layer of security to your account by requiring a 6-digit code at sign-in.</p>
            <button className="btn primary" onClick={startSetup}>Enable 2FA</button>
          </>
        )}

        {status?.enabled && !wizard && (
          <>
            <p>
              <span className="chip status-active" style={{ marginRight: 8 }}>2FA active</span>
              {status.last_used_at && <span className="muted">last used {daysAgo(status.last_used_at)}</span>}
            </p>
            <p className="muted">
              {status.backup_codes_remaining} of {status.backup_codes_total} backup codes remaining.
            </p>
            {!confirmDisable && (
              <button className="btn ghost" onClick={() => setConfirmDisable(true)}>Disable 2FA</button>
            )}
            {confirmDisable && (
              <div style={{ marginTop: 12 }}>
                <p>Enter your current 6-digit code to disable 2FA:</p>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  style={{ width: 120, fontSize: 18, letterSpacing: 4 }}
                />
                <div style={{ marginTop: 8 }}>
                  <button className="btn primary" onClick={disableMfa} disabled={disableCode.length !== 6}>Disable</button>
                  <button className="btn ghost" onClick={() => { setConfirmDisable(false); setDisableCode(''); }} style={{ marginLeft: 6 }}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}

        {wizard && (
          <Wizard
            wizard={wizard}
            onAdvance={(next) => setWizard((w) => ({ ...w, ...next }))}
            onConfirm={confirmCode}
            onClose={() => { setWizard(null); reload(); }}
            error={error}
          />
        )}

        {error && !wizard && <div className="alert error" style={{ marginTop: 12 }}>{error}</div>}
      </div>
    </section>
  );
}

function Wizard({ wizard, onAdvance, onConfirm, onClose, error }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[1, 2, 3].map((n) => (
          <span key={n} className={'chip ' + (wizard.step >= n ? 'status-active' : '')}>Step {n}</span>
        ))}
      </div>

      {wizard.step === 1 && (
        <div>
          <h4>Scan this QR code with your authenticator app</h4>
          {wizard.qr && <img src={wizard.qr} alt="MFA QR code" style={{ width: 180, height: 180, border: '1px solid #e2e8f0' }} />}
          <p className="muted" style={{ fontSize: 12 }}>Can't scan? Enter this code manually:</p>
          <SecretCopy secret={wizard.secret} />
          <div style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={() => onAdvance({ step: 2 })}>Next</button>
            <button className="btn ghost" onClick={onClose} style={{ marginLeft: 6 }}>Cancel</button>
          </div>
        </div>
      )}

      {wizard.step === 2 && (
        <CodeStep onSubmit={onConfirm} onBack={() => onAdvance({ step: 1 })} error={error} />
      )}

      {wizard.step === 3 && wizard.backupCodes && (
        <BackupCodesStep codes={wizard.backupCodes} onDone={onClose} />
      )}
    </div>
  );
}

function SecretCopy({ secret }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <code style={{ background: '#f1f5f9', padding: '6px 10px', borderRadius: 4, fontSize: 13, letterSpacing: 1 }}>{secret}</code>
      <button
        className="btn ghost small"
        onClick={async () => {
          try { await navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 2000); }
          catch { /* ignore */ }
        }}
        aria-label="Copy secret"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

function CodeStep({ onSubmit, onBack, error }) {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  async function submit(e) {
    e?.preventDefault();
    try { await onSubmit(code); } catch { setCode(''); inputRef.current?.focus(); }
  }
  return (
    <form onSubmit={submit}>
      <h4>Enter the 6-digit code from your authenticator app</h4>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        style={{ width: 160, fontSize: 22, letterSpacing: 6, textAlign: 'center' }}
      />
      {error && <div className="alert error" style={{ marginTop: 8 }} role="alert">{error}</div>}
      <div style={{ marginTop: 12 }}>
        <button className="btn ghost" type="button" onClick={onBack}>Back</button>
        <button className="btn primary" type="submit" disabled={code.length !== 6} style={{ marginLeft: 6 }}>Verify</button>
      </div>
    </form>
  );
}

function BackupCodesStep({ codes, onDone }) {
  const [confirmed, setConfirmed] = useState(false);
  function copyAll() { navigator.clipboard.writeText(codes.join('\n')).catch(() => {}); }
  function downloadTxt() {
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'bosplus-mfa-backup-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div>
      <h4>Save these backup codes somewhere safe</h4>
      <p className="muted" style={{ fontSize: 12 }}>Each code can be used once if you lose access to your authenticator app.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontFamily: 'monospace', fontSize: 14, padding: 12, background: '#f1f5f9', borderRadius: 4 }}>
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="btn ghost small" onClick={copyAll}>Copy all codes</button>
        <button className="btn ghost small" onClick={downloadTxt} style={{ marginLeft: 6 }}>Download as .txt</button>
      </div>
      <label style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />{' '}
        I've saved my backup codes
      </label>
      <button className="btn primary" onClick={onDone} disabled={!confirmed} style={{ marginTop: 12 }}>Done</button>
    </div>
  );
}

function daysAgo(iso) {
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}
